// services/scenariosApi.ts — HTTP client for /api/scenarios.
//
// Endpoint and response shape match STORIES_CONTRACT.md §5 (GET /api/scenarios).
// Bearer-authed via authedFetch — the catalogue is post-auth only because
// the `locked` flag is derived from the caller's tier server-side.

import { authedFetch } from "../auth/tokens";

// ── Types — match STORIES_CONTRACT §5 exactly ───────────────────────────

export interface ScenarioListEntry {
  id: string;
  name: string;
  tagline: string;
  /** ~200 chars, shown in the card preview. */
  worldStyle: string;
  tone: string;
  contentRating: "17+";
  /**
   * Thematic per-scenario default story name. Used by NamePrompt as the
   * placeholder + Skip link label so the suggestion reads like a story
   * title ("Smoke and rain") rather than the world's display name
   * ("Ember Hollow"). Comes straight from the scenario file on the server.
   */
  defaultStoryName: string;
  /** Server-derived: true when the scenario is paid-only and the caller is on free. */
  locked: boolean;
}

export interface ScenariosResponse {
  scenarios: ScenarioListEntry[];
}

// ── Error ───────────────────────────────────────────────────────────────

/** Any non-2xx response from /api/scenarios. */
export class ScenariosApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: unknown;
  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = "ScenariosApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

// ── Helper: parse response, throw a ScenariosApiError on non-2xx ────────

interface ServerErrBody {
  error?: string;
  code?: string;
}

async function parseScenarioResponse<T>(res: Response): Promise<T> {
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — fall through.
    }
  }
  if (!res.ok) {
    const errBody = (body as ServerErrBody | null) ?? {};
    const message =
      errBody.error ||
      (text ? text.slice(0, 200) : `Request failed with status ${res.status}`);
    throw new ScenariosApiError(message, res.status, errBody.code, body);
  }
  return body as T;
}

// ── Endpoints ───────────────────────────────────────────────────────────

/**
 * GET /api/scenarios — return the catalogue with each entry's `locked` flag
 * derived from the caller's tier. Free users see the default scenario
 * unlocked and the rest dimmed; paid tiers see everything unlocked.
 */
export async function listScenarios(): Promise<ScenariosResponse> {
  const res = await authedFetch(`/api/scenarios`, { method: "GET" });
  return parseScenarioResponse<ScenariosResponse>(res);
}
